import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Palette, Loader2, Save, RotateCcw } from "lucide-react";
import { finApi, apiErr } from "../api";
import { useFin } from "../FinanceApp";
import { buildInvoiceHtml, DEFAULT_THEME } from "../lib/invoicePrint";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Slider } from "../../components/ui/slider";

const SWATCHES = [["#059669", "#0f172a"], ["#2563eb", "#0b1f4d"], ["#7c3aed", "#1e1b4b"], ["#dc2626", "#3b0a0a"], ["#d97706", "#292524"], ["#0891b2", "#083344"], ["#be185d", "#3f0d24"], ["#334155", "#020617"]];
const STYLES = [["classic", "كلاسيكي", "أشرطة ملوّنة وجداول بحدود"], ["modern", "عصري", "ترويسة داكنة وزوايا دائرية"], ["minimal", "بسيط", "خطوط رفيعة بدون تعبئة"]];
const SAMPLE = { doc_type: "sales_invoice", invoice_kind: "standard", number: "INV-000123", date: "2026-06-15", party_name: "شركة العميل التجريبي", party_vat: "300000000000003",
  _party: { building_number: "1234", street: "الملك فهد", district: "العليا", city: "الرياض", postal_code: "12211", additional_number: "5678" },
  subtotal: 1000, total_discount: 0, total_vat: 150, total: 1150, notes: "",
  lines: [{ description: "خدمة توصيل شهرية", qty: 2, price: 400, discount: 0, taxable: 800, tax_rate: 15, vat: 120, total: 920 },
    { description: "رسوم تشغيل", qty: 1, price: 200, discount: 0, taxable: 200, tax_rate: 15, vat: 30, total: 230 }] };

const Swatch = ({ p, d, active, onClick }) => (
  <button type="button" onClick={onClick} className={`h-9 w-9 overflow-hidden rounded-full border-2 transition-transform hover:scale-110 ${active ? "border-slate-900 ring-2 ring-slate-300" : "border-white shadow"}`} data-testid={`fin-theme-swatch-${p.slice(1)}`}>
    <span className="block h-1/2" style={{ background: p }} /><span className="block h-1/2" style={{ background: d }} />
  </button>
);

const Preview = ({ html }) => {
  const box = useRef(null);
  const inner = useRef(null);
  const [dim, setDim] = useState({ k: 0.5, h: 500 });
  useEffect(() => {
    const fit = () => { if (!box.current || !inner.current) return; const k = Math.min(1, (box.current.clientWidth - 16) / 794); setDim({ k, h: inner.current.offsetHeight * k }); };
    fit();
    const ro = new ResizeObserver(fit); ro.observe(box.current);
    return () => ro.disconnect();
  }, [html]);
  return (
    <div ref={box} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 p-2" data-testid="fin-theme-preview">
      <div style={{ height: dim.h, position: "relative" }}>
        <div ref={inner} className="bg-white shadow" style={{ width: 794, transform: `scale(${dim.k})`, transformOrigin: "top right", position: "absolute", right: 0, top: 0 }} dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
};

export function InvoiceThemeCard() {
  const { user } = useFin();
  const [t, setT] = useState(null);
  const [logo, setLogo] = useState("");
  const [z, setZ] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { finApi.settings().then((s) => { setT({ ...DEFAULT_THEME, ...(s.invoice_theme || {}) }); setLogo(s.logo || ""); setZ(s.zatca || {}); }).catch(() => setT({ ...DEFAULT_THEME })); }, []);
  const c = user?.company || {};
  const html = useMemo(() => t && buildInvoiceHtml(SAMPLE, { theme: t, logo, seller: { ...z, name: z.seller_name || c.name, vat: z.vat_number || c.vat_number, cr: z.cr_number || c.cr_number, city: z.city || c.city, address: z.address || c.address, banks: c.bank_accounts, stamp: c.stamp } }), [t, logo, c, z]);
  if (!t) return null;
  const save = async () => { setBusy(true); try { await finApi.updateSettings({ invoice_theme: t }); toast.success("تم حفظ قالب الفاتورة"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="fin-invoice-theme">
      <div className="space-y-4">
        <div className="flex items-center gap-2 font-bold text-slate-800"><Palette className="h-4 w-4 text-emerald-600" /> قالب الفاتورة</div>
        <div>
          <Label>الألوان</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {SWATCHES.map(([p, d]) => <Swatch key={p} p={p} d={d} active={t.primary === p && t.dark === d} onClick={() => setT({ ...t, primary: p, dark: d })} />)}
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
            <label className="flex items-center gap-2">اللون الرئيسي <input type="color" value={t.primary} onChange={(e) => setT({ ...t, primary: e.target.value })} className="h-8 w-10 cursor-pointer rounded border" data-testid="fin-theme-primary" /></label>
            <label className="flex items-center gap-2">اللون الداكن <input type="color" value={t.dark} onChange={(e) => setT({ ...t, dark: e.target.value })} className="h-8 w-10 cursor-pointer rounded border" data-testid="fin-theme-dark" /></label>
          </div>
        </div>
        <div>
          <Label>التصميم</Label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {STYLES.map(([k, n, d]) => (
              <button key={k} type="button" onClick={() => setT({ ...t, style: k })} data-testid={`fin-theme-style-${k}`}
                className={`rounded-xl border p-2.5 text-start transition-colors ${t.style === k ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="text-sm font-bold">{n}</div><div className="text-[10px] leading-snug text-slate-500">{d}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>مكان الشعار</Label>
          <div className="mt-2 flex gap-2">
            {[["left", "يسار"], ["right", "يمين"]].map(([k, n]) => <button key={k} type="button" onClick={() => setT({ ...t, logo_side: k })} data-testid={`fin-theme-logo-${k}`}
              className={`rounded-lg border px-4 py-1.5 text-sm ${t.logo_side === k ? "border-emerald-500 bg-emerald-50 font-bold" : "border-slate-200"}`}>{n}</button>)}
          </div>
        </div>
        <div data-testid="fin-theme-stamp">
          <Label>الختم: الحجم {t.stamp_size}px</Label>
          <Slider className="mt-3" min={60} max={320} step={10} value={[t.stamp_size]} onValueChange={([v]) => setT({ ...t, stamp_size: v })} data-testid="fin-theme-stamp-size" />
          <div className="mt-3 flex gap-2">
            {[["right", "يمين"], ["center", "وسط"], ["left", "يسار"]].map(([k, n]) => <button key={k} type="button" onClick={() => setT({ ...t, stamp_pos: k })} data-testid={`fin-theme-stamp-${k}`}
              className={`rounded-lg border px-4 py-1.5 text-sm ${t.stamp_pos === k ? "border-emerald-500 bg-emerald-50 font-bold" : "border-slate-200"}`}>{n}</button>)}
          </div>
          {!c.stamp && <p className="mt-1 text-[11px] text-amber-700">ارفع صورة الختم من «بيانات الشركة» أعلاه لتظهر في المعاينة</p>}
        </div>
        <div className="space-y-1.5">
          <Label>نص أسفل الفاتورة (اختياري)</Label>
          <Textarea rows={3} maxLength={400} value={t.footer_text} onChange={(e) => setT({ ...t, footer_text: e.target.value })} placeholder="مثال: الشروط والأحكام، سياسة الاسترجاع، شكراً لتعاملكم معنا" data-testid="fin-theme-footer" />
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="fin-theme-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ القالب</Button>
          <Button variant="ghost" onClick={() => setT({ ...DEFAULT_THEME })} className="gap-1.5" data-testid="fin-theme-reset"><RotateCcw className="h-4 w-4" /> الافتراضي</Button>
        </div>
      </div>
      <div><div className="mb-2 text-xs text-slate-500">معاينة مباشرة</div><Preview html={html} /></div>
    </div>
  );
}
